import { TileData, SeaObjectType } from "../../types/treasure-hunt";
import {
  TILE_SIZE,
  GRID_COLS,
  GRID_ROWS,
  SEA_OBJECT_ICONS,
  SEA_OBJECT_NAMES,
  GIFT_ICONS,
  GIFT_NAMES,
  ROTATABLE_OBJECTS,
  WATER_ONLY_OBJECTS,
} from "../../constants/treasure-hunt";

interface GameFieldProps {
  tileMap: TileData[][];
  hoveredTile: { x: number; y: number } | null;
  selectedPrize?: { x: number; y: number } | null;
  onTileClick: (x: number, y: number) => void;
  onTileDoubleClick?: (x: number, y: number) => void;
  onTileHover: (x: number, y: number, event?: React.MouseEvent) => void;
  onTileLeave: () => void;
  onRotate: (x: number, y: number) => void;
  showRotateHint?: boolean;
  isPromoCodeEntered?: boolean;
  interactiveTiles?: boolean[][];
  tileSize?: number;
  showClouds?: boolean; // Показывать ли слой облаков
  cloudEditMode?: boolean; // Режим редактирования облаков
}

export const GameField = ({
  tileMap,
  hoveredTile,
  selectedPrize,
  onTileClick,
  onTileDoubleClick,
  onTileHover,
  onTileLeave,
  onRotate,
  showRotateHint = true,
  isPromoCodeEntered = false,
  interactiveTiles = [],
  tileSize = TILE_SIZE,
  showClouds = false,
  cloudEditMode = false,
}: GameFieldProps) => {
  const gridWidth = GRID_COLS * tileSize;
  const gridHeight = GRID_ROWS * tileSize;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${GRID_COLS}, ${tileSize}px)`,
        gridTemplateRows: `repeat(${GRID_ROWS}, ${tileSize}px)`,
        width: `${gridWidth}px`,
        height: `${gridHeight}px`,
        lineHeight: 0,
        gap: "-1px",
        overflow: "hidden",
        isolation: "isolate",
      }}
    >
      {tileMap.map((row, y) =>
        row.map((tile, x) => {
          // Определяем фон клетки на основе tileCode
          // Если tileCode есть и это WATER или объект только для воды - показываем воду
          // Иначе показываем песок
          const isWaterTile =
            tile.tileCode === SeaObjectType.WATER ||
            (tile.tileCode && WATER_ONLY_OBJECTS.includes(tile.tileCode));
          const backgroundImage = isWaterTile
            ? "url(/models/treasure-hunt/sea.jpg)"
            : "url(/models/treasure-hunt/sand-island.jpg)";

          return (
            <div
              key={`${x}-${y}`}
              data-tile-x={x}
              data-tile-y={y}
              className="transition-all relative cursor-pointer"
              style={{
                width: `${tileSize + 1}px`,
                height: `${tileSize + 1}px`,
                backgroundImage,
                backgroundSize: "cover",
                backgroundPosition: "center",
                opacity: hoveredTile?.x === x && hoveredTile?.y === y ? 0.8 : 1,
                transform:
                  hoveredTile?.x === x && hoveredTile?.y === y
                    ? "scale(1.05)"
                    : "scale(1)",
                border:
                  selectedPrize?.x === x && selectedPrize?.y === y
                    ? "2px solid yellow"
                    : "none",
                outline: "none",
                boxSizing: "border-box",
                marginRight: "-1px",
                marginBottom: "-1px",
                imageRendering: "crisp-edges",
              }}
              onMouseEnter={(e) => onTileHover(x, y, e)}
              onMouseLeave={onTileLeave}
              onClick={() => onTileClick(x, y)}
              onDoubleClick={() => onTileDoubleClick?.(x, y)}
              onContextMenu={(e) => {
                e.preventDefault();
                onRotate(x, y);
              }}
            >
              {/* Рамка при наведении */}
              {hoveredTile?.x === x && hoveredTile?.y === y && (
                <div
                  className="absolute inset-0 pointer-events-none z-30"
                  style={{
                    backgroundImage:
                      "url(/models/treasure-hunt/ui/square-frame.png)",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
              )}
              {/* Если есть морской предмет, показываем его */}
              {tile.seaObject && (
                <>
                  <img
                    src={SEA_OBJECT_ICONS[tile.seaObject.type]}
                    alt={SEA_OBJECT_NAMES[tile.seaObject.type]}
                    className="w-full h-full object-cover pointer-events-none"
                    style={{
                      transform: `rotate(${tile.seaObject.rotation}deg)`,
                      display: "block",
                      outline: "none",
                    }}
                  />
                  {/* Стрелка поворота при наведении */}
                  {showRotateHint &&
                    hoveredTile?.x === x &&
                    hoveredTile?.y === y &&
                    ROTATABLE_OBJECTS.includes(tile.seaObject.type) && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        {/* Иконка поворота - круговая стрелка */}
                        <svg
                          width="36"
                          height="36"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="text-white drop-shadow-lg"
                        >
                          <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38" />
                        </svg>
                        {/* Текст подсказки */}
                        <div className="absolute bottom-1 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                          ПКМ
                        </div>
                      </div>
                    )}
                </>
              )}
              {/* Если есть подарок, показываем иконку */}
              {tile.gift && (
                <img
                  src={GIFT_ICONS[tile.gift.type]}
                  alt={GIFT_NAMES[tile.gift.type]}
                  className="w-full h-full object-contain"
                  title={tile.gift.text}
                  style={{
                    display: "block",
                    outline: "none",
                  }}
                />
              )}
              {/* Слой облаков поверх всего содержимого */}
              {showClouds && tile.hasClouds && (
                <div
                  className="absolute inset-0 pointer-events-none z-20"
                  style={{
                    backgroundImage: "url(/models/treasure-hunt/cloud.jpg)",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    opacity:
                      cloudEditMode &&
                      hoveredTile?.x === x &&
                      hoveredTile?.y === y
                        ? 0.5
                        : 1,
                  }}
                />
              )}
              {/* Индикатор режима редактирования облаков */}
              {cloudEditMode &&
                hoveredTile?.x === x &&
                hoveredTile?.y === y && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-30">
                    <div className="bg-black bg-opacity-75 text-white text-xs px-3 py-2 rounded-lg font-semibold">
                      {tile.hasClouds
                        ? "❌ Убрать облако"
                        : "✅ Добавить облако"}
                    </div>
                  </div>
                )}
            </div>
          );
        }),
      )}
    </div>
  );
};
